import { useState } from 'react';
import { Alert, Modal } from '@gupshup-frontend/gupshup-ts-components';
import { deleteTemplate, templateErrorMessage } from '../../api/templates';
import { statusMeta } from '../../helper/templateStatus';

// deleteTemplate (api/templates.js) throws synchronously if `type` is falsy — that endpoint's
// `type` query param must be the row's own template.type, never a hardcoded value (e.g. 'TEXT'),
// so a template missing it can't be deleted here at all. Surfaced as a normal inline error below
// instead of letting that throw become an unhandled rejection.
const MISSING_TYPE_MESSAGE = "This template has no type on record, so it can't be deleted from here.";

// Confirms deletion of one template. Mounted by Templates/index.js only while
// pendingAction.intent === 'delete'; onClose is how the parent unmounts it.
const DeleteModal = ({
  orgId, channelId, template, onClose, onDeleted, onError,
}) => {
  const [deleting, setDeleting] = useState(false);
  const missingType = !template?.type;
  const [error, setError] = useState(() => (missingType ? MISSING_TYPE_MESSAGE : null));

  const meta = statusMeta(template?.status);

  // Backdrop click, Escape and the dialog's own close icon all funnel through MUI's onClose,
  // same as Cancel below — guarded so a request in flight can't be abandoned mid-air (this
  // component would unmount while its own .catch below still had a setState queued against it).
  const handleClose = () => {
    if (deleting) return;
    onClose();
  };

  const handleConfirm = async () => {
    if (missingType) return; // Confirm stays disabled in this state too; nothing to call.
    setDeleting(true);
    setError(null);
    try {
      await deleteTemplate(orgId, channelId, String(template.id), template.type);
      // Reached only on a confirmed success — never remove the row optimistically. The upstream
      // can still reject the delete while gs-intg's wrapper answers HTTP 200 (see
      // templateErrorMessage's own comment in api/templates.js), so the list must only change here.
      onDeleted();
      onClose();
    } catch (err) {
      const message = templateErrorMessage(err);
      setDeleting(false);
      setError(message);
      onError?.(message);
    }
  };

  return (
    <Modal
      data={{
        title: 'Delete template?',
        content: (
          <>
            <p>
              You are about to delete <strong>{template?.name}</strong> — {meta.label}.
            </p>
            <p>This deletes the template from your WhatsApp account, not just this list.</p>
            {error && <Alert type="error" description={error} data-testid="bloomreach-templates-delete-error" />}
          </>
        ),
        actionButtonProps: [
          {
            children: 'Cancel',
            variant: 'outlined',
            disabled: deleting,
            onClick: handleClose,
            'data-testid': 'bloomreach-templates-delete-cancel',
          },
          {
            children: 'Delete',
            variant: 'contained',
            color: 'error',
            loading: deleting,
            disabled: deleting || missingType,
            onClick: handleConfirm,
            'data-testid': 'bloomreach-templates-delete-confirm',
          },
        ],
      }}
      config={{
        open: true,
        onClose: handleClose,
        showCloseIcon: true,
        closeIconTestId: 'bloomreach-templates-delete-close',
        'data-testid': 'bloomreach-templates-delete-modal',
      }}
    />
  );
};

export default DeleteModal;
